import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaHeart, FaTrash, FaMinus, FaPlus, FaTimes } from 'react-icons/fa';
import '../styles/CartSidebar.css';

const CartSidebar = ({
  isOpen,
  cartItems,
  onClose,
  onUpdateQuantity,
  onRemoveItem,
  onCheckout: _onCheckout,
  onSaveForLater,
}) => {
  const navigate = useNavigate();
  const isLoggedIn = !!localStorage.getItem('token');
  const [promoCode, setPromoCode] = useState('');
  const [appliedCode, setAppliedCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [promoError, setPromoError] = useState('');
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);
  const [animatingItems, setAnimatingItems] = useState(new Set());

  // Animation for adding/removing items
  const animateItemChange = (itemId, _action) => {
    setAnimatingItems(prev => new Set([...prev, itemId]));
    setTimeout(() => {
      setAnimatingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }, 300);
  };

  const calculateSubtotal = () => {
    return cartItems.reduce((total, item) => {
      const price =
        typeof item.price === 'string' ? parseFloat(item.price.replace('$', '')) : item.price;
      return total + price * item.quantity;
    }, 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    return subtotal - promoDiscount;
  };

  const handleQuantityChange = (itemId, newQuantity) => {
    if (newQuantity < 1) {
      return;
    }
    if (newQuantity > 99) {
      return;
    } // Max quantity validation

    animateItemChange(itemId, 'update');
    onUpdateQuantity(itemId, newQuantity);
  };

  const handleRemoveItem = itemId => {
    animateItemChange(itemId, 'remove');
    setTimeout(() => onRemoveItem(itemId), 150);
  };

  const handleSaveForLater = async item => {
    if (!isLoggedIn) {
      navigate('/auth', {
        state: {
          returnTo: window.location.pathname,
          action: 'save_for_later',
        },
      });
      return;
    }

    try {
      // Add to wishlist
      const response = await fetch('/api/wishlist/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ product_id: item.id }),
      });

      if (response.ok) {
        // Remove from cart after successful wishlist addition
        handleRemoveItem(item.id);
        if (onSaveForLater) {
          onSaveForLater(item);
        }
      } else if (response.status === 409) {
        // Already in wishlist, just remove from cart
        handleRemoveItem(item.id);
      }
    } catch (error) {
      if (window.showToast) {
        window.showToast('Could not save this item for later', { type: 'error' });
      }
    }
  };

  const applyPromoCode = async () => {
    if (!promoCode.trim()) {
      return;
    }

    setIsApplyingPromo(true);
    setPromoError('');

    try {
      const response = await fetch('/api/promotions/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: promoCode,
          subtotal: calculateSubtotal(),
          items: cartItems.map(item => ({
            price:
              typeof item.price === 'string'
                ? parseFloat(item.price.replace('$', ''))
                : item.price,
            quantity: item.quantity,
            category: item.category,
          })),
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.valid) {
        setPromoError(data.message || 'Invalid promo code');
        setPromoDiscount(0);
        setAppliedCode('');
      } else {
        setPromoDiscount(data.discount || 0);
        setAppliedCode(data.code);
        setPromoError('');
        setPromoCode('');
      }
    } catch (error) {
      setPromoError('Failed to apply promo code');
    } finally {
      setIsApplyingPromo(false);
    }
  };

  const removePromoCode = () => {
    setPromoCode('');
    setAppliedCode('');
    setPromoDiscount(0);
    setPromoError('');
  };

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      return;
    }
    navigate('/checkout', { state: { promoCode: appliedCode } });
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && <div className='cart-backdrop' onClick={onClose} />}

      <div className={`cart-sidebar ${isOpen ? 'open' : ''}`}>
        <div className='cart-header'>
          <h2>Shopping Cart ({cartItems.length})</h2>
          <button className='close-btn' onClick={onClose} aria-label='Close cart'>
            <FaTimes />
          </button>
        </div>

        <div className='cart-content'>
          <div className='cart-items'>
            {cartItems.length > 0 ? (
              cartItems.map(item => (
                <div
                  key={item.lineId || item.id}
                  className={`cart-item ${animatingItems.has(item.lineId || item.id) ? 'animating' : ''}`}
                >
                  <div className='item-image-container'>
                    <img src={item.image || item.image_url} alt={item.name} className='item-image' />
                  </div>

                  <div className='item-details'>
                    <h3 className='item-name'>{item.name}</h3>
                    {item.size && <p className='item-variant'>Size: {item.size}</p>}
                    {item.color && <p className='item-variant'>Color: {item.color}</p>}
                    <p className='item-price'>
                      $
                      {typeof item.price === 'string'
                        ? parseFloat(item.price.replace('$', '')).toFixed(2)
                        : item.price.toFixed(2)}
                    </p>
                  </div>

                  <div className='item-actions'>
                    <div className='quantity-controls'>
                      <button
                        className='quantity-btn'
                        onClick={() => handleQuantityChange(item.lineId || item.id, item.quantity - 1)}
                        disabled={item.quantity <= 1}
                        aria-label='Decrease quantity'
                      >
                        <FaMinus />
                      </button>
                      <input
                        type='number'
                        value={item.quantity}
                        onChange={e =>
                          handleQuantityChange(item.lineId || item.id, parseInt(e.target.value) || 1)
                        }
                        className='quantity-input'
                        min='1'
                        max='99'
                      />
                      <button
                        className='quantity-btn'
                        onClick={() => handleQuantityChange(item.lineId || item.id, item.quantity + 1)}
                        disabled={item.quantity >= 99}
                        aria-label='Increase quantity'
                      >
                        <FaPlus />
                      </button>
                    </div>

                    <div className='item-buttons'>
                      <button
                        className='save-later-btn'
                        onClick={() => handleSaveForLater(item)}
                        title='Save for later'
                      >
                        <FaHeart />
                      </button>
                      <button
                        className='remove-btn'
                        onClick={() => handleRemoveItem(item.lineId || item.id)}
                        title='Remove item'
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className='empty-cart'>
                <p>Your cart is empty</p>
                <button
                  className='continue-shopping-btn'
                  onClick={() => {
                    onClose();
                    navigate('/shop');
                  }}
                >
                  Continue Shopping
                </button>
              </div>
            )}
          </div>

          {cartItems.length > 0 && (
            <div className='cart-footer'>
              {/* Promo Code Section */}
              <div className='promo-section'>
                <div className='promo-input-group'>
                  <input
                    type='text'
                    placeholder='Enter promo code'
                    value={promoCode}
                    onChange={e => setPromoCode(e.target.value)}
                    className='promo-input'
                    disabled={isApplyingPromo}
                  />
                  <button
                    className='apply-promo-btn'
                    onClick={applyPromoCode}
                    disabled={!promoCode.trim() || isApplyingPromo}
                  >
                    {isApplyingPromo ? 'Applying...' : 'Apply'}
                  </button>
                </div>
                {promoError && <p className='promo-error'>{promoError}</p>}
                {promoDiscount > 0 && (
                  <div className='promo-success'>
                    <span>Promo code applied: -{promoDiscount.toFixed(2)}</span>
                    <button onClick={removePromoCode} className='remove-promo-btn'>
                      <FaTimes />
                    </button>
                  </div>
                )}
              </div>

              {/* Cart Summary */}
              <div className='cart-summary'>
                <div className='summary-row'>
                  <span>Subtotal:</span>
                  <span>${calculateSubtotal().toFixed(2)}</span>
                </div>
                {promoDiscount > 0 && (
                  <div className='summary-row discount'>
                    <span>Discount:</span>
                    <span>-${promoDiscount.toFixed(2)}</span>
                  </div>
                )}
                <div className='summary-row total'>
                  <span>Total:</span>
                  <span>${calculateTotal().toFixed(2)}</span>
                </div>
                <p className='shipping-note'>Shipping calculated at checkout</p>
              </div>

              <button className='checkout-btn' onClick={handleCheckout}>
                Proceed to Checkout
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CartSidebar;
